import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  fetchTranslationLanguages,
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_SUBQUESTIONS,
  translateBulk,
} from '@api/instant_survey';
import { SURVEY_SOURCE_LANGUAGE, SURVEY_TRANSLATION_LANGUAGE_LABELS } from '@constants';
import { getSurveyLanguageSelection } from '@helpers';

import {
  Button,
  Checkbox,
  ModalV2 as Modal,
  OverlayPopover,
  Radio,
  Select,
  toast,
} from '@utilities';

import ConceptTranslations from './components/ConceptTranslations';
import QuestionTranslations from './components/QuestionTranslations';

import { isLanguageScreenerQuestion } from '../../../../utilities/helpers';

import { planLanguageScreenerReconciliation } from './utilities/languageScreener';
import { getTranslationModalMessage } from './utilities/getTranslationModalMessage';

import {
  clearTranslationDraft,
  getTranslationDraft,
  setTranslationDraft,
} from './utilities/translationDraftStore';
import {
  buildSurveyUpdates,
  getEntityKeyFromTranslationKey,
  getInitialTranslations,
  getSourceTextByEntityKey,
  hasTranslationText,
  seedTranslations,
} from './utilities/translationSync';
import {
  addPendingTranslationKeys,
  removePendingTranslationKeys,
} from './utilities/pendingTranslationKeys';
import { useOnScreenTranslation } from './utilities/useOnScreenTranslation';

import styles from './_index.module.scss';

const MAX_LANGUAGES = 2;

const DETECTED_LANGUAGE = SURVEY_SOURCE_LANGUAGE;

const FALLBACK_LANGUAGE_LABELS = SURVEY_TRANSLATION_LANGUAGE_LABELS;

const areLanguageOptionsEqual = (a, b) =>
  a.length === b.length &&
  a.every(({ label, value }, index) => label === b[index]?.label && value === b[index]?.value);

const isMaxDiffQuestion = (question) => question.type === QUESTION_TYPES.MAX_DIFF.value;

const getTranslationKeysFromItems = (items, languages) => {
  const keys = [];
  languages.forEach((language) => {
    Object.keys(items.concepts || {}).forEach((conceptId) => {
      keys.push(`concept-${conceptId}:${language}`);
    });
    Object.entries(items.questions || {}).forEach(([questionId, entry]) => {
      if (entry?.text) keys.push(`question-${questionId}:${language}`);
      if (entry?.properties?.bestLabel) {
        keys.push(`maxdiff-best-label-${questionId}:${language}`);
      }
      if (entry?.properties?.worstLabel) {
        keys.push(`maxdiff-worst-label-${questionId}:${language}`);
      }
      Object.keys(entry?.options || {}).forEach((optionId) => {
        keys.push(`option-${questionId}-${optionId}:${language}`);
      });
      Object.keys(entry?.subQuestions || {}).forEach((subQuestionId) => {
        keys.push(`subquestion-${questionId}-${subQuestionId}:${language}`);
      });
    });
  });
  return keys;
};

const ModalTranslation = ({ onClose, onLanguageInfoChange, onSubmit, survey }) => {
  const concepts = survey.groups || [];
  const questions = survey.questions || [];

  // Show the concept-name token control only when the survey uses concept names.
  const hasConceptName = concepts.some((group) => group?.conceptName != null);
  const [languageOptions, setLanguageOptions] = useState([]);

  const languageLabels = useMemo(
    () => ({
      ...FALLBACK_LANGUAGE_LABELS,
      ...Object.fromEntries(languageOptions.map(({ label, value }) => [value, label])),
    }),
    [languageOptions]
  );

  const baseLanguageOptions = useMemo(
    () => [
      { label: 'English', value: '' },
      ...languageOptions.filter(({ value }) => value !== DETECTED_LANGUAGE),
    ],
    [languageOptions]
  );

  // Null language_info means no user-selected translation languages, even if saved questions already
  // contain baked-in translations.
  const normalizedLanguageSelection = getSurveyLanguageSelection(survey.languageInfo);
  const languageInfo = {
    ...(survey.languageInfo || {}),
    ...(normalizedLanguageSelection.originalLanguage
      ? { originalLanguage: normalizedLanguageSelection.originalLanguage }
      : {}),
    translatedLanguages: normalizedLanguageSelection.translatedLanguages,
  };
  const initialTranslatedLanguages = useMemo(
    () => (languageInfo.translatedLanguages || []).slice(0, MAX_LANGUAGES),
    []
  );

  const baseLanguageTarget = useRef(null);
  const languageMenuTarget = useRef(null);
  const containerRef = useRef(null);
  const conceptTranslationInFlightRef = useRef(new Set());
  // Lets async translation callbacks preserve user-triggered results after the modal closes.
  const isMountedRef = useRef(true);
  // A stored 'en' maps to the default English source instead of a selected source language.
  const [originalLanguage, setOriginalLanguage] = useState(
    languageInfo.originalLanguage && languageInfo.originalLanguage !== DETECTED_LANGUAGE
      ? languageInfo.originalLanguage
      : null
  );
  const [isBaseMenuOpen, setIsBaseMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [pendingManualTranslationKeys, setPendingManualTranslationKeys] = useState(new Set());
  const translationRequestCountRef = useRef(0);
  const [pendingBaseLanguage, setPendingBaseLanguage] = useState(originalLanguage || '');
  const [pendingLanguages, setPendingLanguages] = useState([]);
  const [appliedLanguages, setAppliedLanguages] = useState(initialTranslatedLanguages);
  const [isApplying, setIsApplying] = useState(false);
  // Last committed language selection, used to restore UI state if screener sync fails.
  const committedLanguageInfoRef = useRef({
    originalLanguage: languageInfo.originalLanguage || DETECTED_LANGUAGE,
    translatedLanguages: initialTranslatedLanguages,
  });
  const languageSyncRequestRef = useRef(0);
  const [languageSyncStatus, setLanguageSyncStatus] = useState('idle');
  // Local translation buffer; persisted onto the survey only when Apply Changes succeeds.
  const [translations, setTranslations] = useState(() =>
    seedTranslations(survey, getTranslationDraft(survey.id))
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchTranslationLanguages({ signal: controller.signal })
      .then((options) => {
        if (!options.length) return;
        setLanguageOptions((prev) => (areLanguageOptionsEqual(prev, options) ? prev : options));
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error.name === 'CanceledError') return;
        console.error('Error fetching translation languages', error);
      });

    return () => {
      controller.abort();
    };
  }, []);

  // Keep un-applied edits in the module-scoped draft so they survive closing the modal.
  useEffect(() => {
    setTranslationDraft(survey.id, translations);
  }, [survey.id, translations]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Async translation batches need the latest buffer to avoid overwriting user edits.
  const translationsRef = useRef(translations);
  translationsRef.current = translations;

  const sourceLanguage = originalLanguage || DETECTED_LANGUAGE;
  const baseLanguageLabel = originalLanguage ? languageLabels[originalLanguage] : 'English';
  const getLanguageOption = (language) =>
    languageOptions.find(({ value }) => value === language) || {
      label: languageLabels[language] || language,
      value: language,
    };

  const buildLanguageInfo = (source, targets) => ({
    originalLanguage: source || DETECTED_LANGUAGE,
    translatedLanguages: targets,
  });

  const currentLanguageInfo = useMemo(
    () => buildLanguageInfo(originalLanguage, appliedLanguages),
    [appliedLanguages, originalLanguage]
  );

  const syncedQuestions = useMemo(() => {
    const plan = planLanguageScreenerReconciliation(questions, currentLanguageInfo);
    return plan.type === 'set' ? plan.questions : questions;
  }, [currentLanguageInfo, questions]);

  // Display only active questions, but keep the full `syncedQuestions` list for buildSurveyUpdates
  // so questions pending deletion are still written back to the survey on Apply.
  const activeQuestions = syncedQuestions.filter((question) => !question._delete);

  const languageScreenerEntityKeys = useMemo(() => {
    const keys = new Set();
    syncedQuestions.forEach((question) => {
      if (question._delete || !isLanguageScreenerQuestion(question)) return;
      keys.add(`question-${question.id}`);
      if (isMaxDiffQuestion(question)) {
        keys.add(`maxdiff-best-label-${question.id}`);
        keys.add(`maxdiff-worst-label-${question.id}`);
      }
      (question.answers || []).forEach((answer) => {
        if (!answer._delete) keys.add(`option-${question.id}-${answer.id}`);
      });
    });
    return keys;
  }, [syncedQuestions]);

  // Commit only after managed screener sync succeeds; failures restore the last committed selection.
  const commitLanguageInfo = async (source, targets) => {
    const nextLanguageInfo = buildLanguageInfo(source, targets);
    const requestId = languageSyncRequestRef.current + 1;
    languageSyncRequestRef.current = requestId;
    setLanguageSyncStatus('pending');

    let didSync = false;
    try {
      didSync = (await onLanguageInfoChange(nextLanguageInfo)) === true;
    } catch (error) {
      console.error('Error synchronizing survey languages', error);
    }

    if (requestId !== languageSyncRequestRef.current) return false;

    if (didSync) {
      committedLanguageInfoRef.current = nextLanguageInfo;
      return true;
    }

    const committed = committedLanguageInfoRef.current;
    const committedOriginalLanguage =
      committed.originalLanguage && committed.originalLanguage !== DETECTED_LANGUAGE
        ? committed.originalLanguage
        : null;
    setOriginalLanguage(committedOriginalLanguage);
    setAppliedLanguages([...(committed.translatedLanguages || [])]);
    setPendingBaseLanguage(committedOriginalLanguage || '');
    setPendingLanguages([...(committed.translatedLanguages || [])]);
    setLanguageSyncStatus('failed');
    return false;
  };

  // Source-language changes stale existing translations; machine ones are refetched, user edits warn.
  const invalidateTranslationsForSourceChange = () => {
    setTranslations((prev) => {
      let didChange = false;
      const next = {};
      Object.entries(prev).forEach(([key, entry]) => {
        const entityKey = getEntityKeyFromTranslationKey(key);
        if (languageScreenerEntityKeys.has(entityKey) || entry?.isRelevant === false) {
          next[key] = entry;
          return;
        }
        next[key] = { ...entry, isRelevant: false };
        didChange = true;
      });
      return didChange ? next : prev;
    });
  };

  const applyBaseLanguage = async () => {
    const next = pendingBaseLanguage || null;
    setIsBaseMenuOpen(false);
    if (next === originalLanguage) {
      setLanguageSyncStatus('idle');
      return;
    }
    const resolved = next || DETECTED_LANGUAGE;
    const nextTranslated = appliedLanguages.filter((language) => language !== resolved);
    if (!(await commitLanguageInfo(next, nextTranslated))) return;

    if (resolved !== sourceLanguage) invalidateTranslationsForSourceChange();
    setOriginalLanguage(next);
    if (nextTranslated.length !== appliedLanguages.length) {
      setAppliedLanguages(nextTranslated);
      removeLanguages([resolved]);
    }
    setLanguageSyncStatus('idle');
  };

  const openBaseLanguageMenu = () => {
    setIsLanguageMenuOpen(false);
    setPendingBaseLanguage(originalLanguage || '');
    setIsBaseMenuOpen((open) => !open);
  };

  const openLanguageMenu = () => {
    setIsBaseMenuOpen(false);
    setPendingLanguages(appliedLanguages);
    setIsLanguageMenuOpen(true);
  };

  const togglePendingLanguage = (value) => {
    setPendingLanguages((prev) => {
      if (prev.includes(value)) return prev.filter((language) => language !== value);
      if (prev.length >= MAX_LANGUAGES) return prev;
      return [...prev, value];
    });
  };

  const applyLanguages = async () => {
    const nextLanguages = [...pendingLanguages];
    const hasChanged =
      nextLanguages.length !== appliedLanguages.length ||
      nextLanguages.some((language, index) => language !== appliedLanguages[index]);
    setIsLanguageMenuOpen(false);
    if (!hasChanged) {
      setLanguageSyncStatus('idle');
      return;
    }

    const newlyRemoved = appliedLanguages.filter((language) => !nextLanguages.includes(language));
    if (!(await commitLanguageInfo(originalLanguage, nextLanguages))) return;

    setAppliedLanguages(nextLanguages);
    if (newlyRemoved.length) removeLanguages(newlyRemoved);
    setLanguageSyncStatus('idle');
  };

  // Keep the current column language selectable while excluding already-used target languages.
  const languageOptionsFor = (current) => {
    const options = languageOptions.filter(
      ({ value }) =>
        value === current || (value !== sourceLanguage && !appliedLanguages.includes(value))
    );
    return options.some(({ value }) => value === current)
      ? options
      : [getLanguageOption(current), ...options];
  };

  // Changing a target language drops its old local translations and lets the queue fetch the new one.
  const changeAppliedLanguage = async (previous, option) => {
    const next = option?.value;
    if (!next || next === previous || appliedLanguages.includes(next)) return;
    const nextApplied = appliedLanguages.map((language) =>
      language === previous ? next : language
    );
    if (!(await commitLanguageInfo(originalLanguage, nextApplied))) return;

    setAppliedLanguages(nextApplied);
    removeLanguages([previous]);
    setLanguageSyncStatus('idle');
  };

  // Remove committed languages from local state; Apply marks their saved rows with `_delete: true`.
  const removeLanguages = (languages) => {
    setTranslations((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(
          ([key]) => !languages.some((language) => key.endsWith(`:${language}`))
        )
      )
    );
  };

  const getTranslation = (entityKey, language) =>
    translations[`${entityKey}:${language}`]?.richText || '';

  const getEntry = (entityKey, language) => translations[`${entityKey}:${language}`] || null;

  const sourceTextByEntityKey = getSourceTextByEntityKey(survey);
  const sourceTextSignature = Object.entries(sourceTextByEntityKey)
    .map(([key, value]) => `${key}:${value}`)
    .join('|');
  const getSourceTextForEntityKey = (entityKey) => sourceTextByEntityKey[entityKey] || '';

  useEffect(() => {
    setTranslations((prev) => {
      let didChange = false;
      const next = { ...prev };

      Object.entries(prev).forEach(([key, entry]) => {
        if (!entry?.isUserUpdated || !hasTranslationText(entry.richText)) return;

        const entityKey = getEntityKeyFromTranslationKey(key);
        const currentSourceText = sourceTextByEntityKey[entityKey] || '';
        if (entry.sourceText == null) {
          next[key] = { ...entry, sourceText: currentSourceText };
          didChange = true;
          return;
        }
        if (entry.sourceText !== currentSourceText && entry.isRelevant !== false) {
          next[key] = { ...entry, isRelevant: false, sourceText: currentSourceText };
          didChange = true;
        }
      });

      return didChange ? next : prev;
    });
  }, [sourceTextSignature]);

  // Language screener sync can add/remove question entities while the modal is open.
  const activeQuestionEntityKeys = useMemo(() => {
    const keys = new Set();
    syncedQuestions.forEach((question) => {
      if (question._delete) return;
      keys.add(`question-${question.id}`);
      if (isMaxDiffQuestion(question)) {
        if (question.properties?.bestLabel) keys.add(`maxdiff-best-label-${question.id}`);
        if (question.properties?.worstLabel) keys.add(`maxdiff-worst-label-${question.id}`);
      }
      (question.answers || []).forEach((answer) => {
        if (answer._delete) return;
        keys.add(`option-${question.id}-${answer.id}`);
      });
      if (QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question.type)) {
        (question.subQuestions || []).forEach((subQuestion) => {
          if (subQuestion._delete) return;
          keys.add(`subquestion-${question.id}-${subQuestion.id}`);
        });
      }
    });
    return keys;
  }, [syncedQuestions]);

  // Drop local question translations for entities no longer on the survey.
  useEffect(() => {
    setTranslations((prev) => {
      let didChange = false;
      const next = {};
      Object.entries(prev).forEach(([key, entry]) => {
        const entityKey = getEntityKeyFromTranslationKey(key);
        const isQuestionEntity =
          entityKey.startsWith('question-') ||
          entityKey.startsWith('option-') ||
          entityKey.startsWith('subquestion-') ||
          entityKey.startsWith('maxdiff-');
        if (!isQuestionEntity || activeQuestionEntityKeys.has(entityKey)) {
          next[key] = entry;
        } else {
          didChange = true;
        }
      });
      return didChange ? next : prev;
    });
  }, [activeQuestionEntityKeys]);

  // Merge newly-added survey translations without resurrecting translations the user cleared locally.
  const prevStoredTranslationKeysRef = useRef(null);
  useEffect(() => {
    const stored = getInitialTranslations({ questions: syncedQuestions });
    const storedKeys = Object.keys(stored);
    const prevKeys = prevStoredTranslationKeysRef.current;
    prevStoredTranslationKeysRef.current = new Set(storedKeys);
    if (prevKeys === null) return;
    const addedKeys = storedKeys.filter((key) => !prevKeys.has(key));
    if (!addedKeys.length) return;
    setTranslations((prev) => {
      let didChange = false;
      const next = { ...prev };
      addedKeys.forEach((key) => {
        if (!(key in next)) {
          next[key] = stored[key];
          didChange = true;
        }
      });
      return didChange ? next : prev;
    });
  }, [syncedQuestions]);

  // Store machine translations as relevant, non-user-edited entries. Background translation preserves
  // user edits; explicit Reset/Translate actions can overwrite them.
  const applyTranslatedItems = (items, prev, language, { preserveUserEdits = false } = {}) => {
    const next = { ...prev };
    const setAuto = (key, value) => {
      if (preserveUserEdits && next[key]?.isUserUpdated && hasTranslationText(next[key].richText)) {
        return;
      }
      next[key] = {
        isRelevant: true,
        isUserUpdated: false,
        richText: value,
        sourceText: getSourceTextForEntityKey(getEntityKeyFromTranslationKey(key)),
      };
    };
    Object.entries(items.concepts || {}).forEach(([id, value]) => {
      if (value) setAuto(`concept-${id}:${language}`, value);
    });
    Object.entries(items.questions || {}).forEach(([id, entry]) => {
      if (entry?.text) setAuto(`question-${id}:${language}`, entry.text);
      if (entry?.properties?.bestLabel) {
        setAuto(`maxdiff-best-label-${id}:${language}`, entry.properties.bestLabel);
      }
      if (entry?.properties?.worstLabel) {
        setAuto(`maxdiff-worst-label-${id}:${language}`, entry.properties.worstLabel);
      }
      Object.entries(entry?.options || {}).forEach(([optionId, value]) => {
        if (value) setAuto(`option-${id}-${optionId}:${language}`, value);
      });
      Object.entries(entry?.subQuestions || {}).forEach(([subQuestionId, value]) => {
        if (value) setAuto(`subquestion-${id}-${subQuestionId}:${language}`, value);
      });
    });
    return next;
  };

  const applyQueuedTranslations = (results) => {
    setTranslations((prev) => {
      let next = prev;
      results.forEach(({ language, translatedItems }) => {
        next = applyTranslatedItems(translatedItems, next, language, { preserveUserEdits: true });
      });
      return next;
    });
  };

  // Lazily translates visible question sections instead of one large upfront request.
  const {
    isTranslating: isQueueTranslating,
    pendingTranslationKeys: pendingQueuedTranslationKeys = new Set(),
    prioritizeQuestion,
    registerQuestion,
  } = useOnScreenTranslation({
    appliedLanguages,
    applyTranslations: applyQueuedTranslations,
    scrollRootRef: containerRef,
    sourceLanguage,
    survey,
    translationsRef,
  });
  const isTranslationLoading = isTranslating || isQueueTranslating;
  const pendingTranslationKeys = useMemo(
    () => new Set([...pendingQueuedTranslationKeys, ...pendingManualTranslationKeys]),
    [pendingManualTranslationKeys, pendingQueuedTranslationKeys]
  );

  // Programmatic editor echoes keep machine translations non-user-edited; real text changes promote
  // the entry to user-edited until Reset/Clear/Dismiss explicitly changes that state.
  const handleTranslationChange = (entityKey, language, value) => {
    setTranslations((prev) => {
      const key = `${entityKey}:${language}`;
      if (!hasTranslationText(value)) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      const existing = prev[key];
      const isManualEdit = !existing || existing.richText !== value;
      return {
        ...prev,
        [key]: {
          isRelevant: existing?.isRelevant === false ? false : true,
          isUserUpdated: isManualEdit || existing?.isUserUpdated === true,
          richText: value,
          sourceText: getSourceTextForEntityKey(entityKey),
        },
      };
    });
  };

  // Dismiss accepts the current text and treats it like the latest non-manual translation again.
  const dismissRelevanceWarning = (entityKey, language) => {
    setTranslations((prev) => {
      const key = `${entityKey}:${language}`;
      const entry = prev[key];
      if (!entry) return prev;
      return { ...prev, [key]: { ...entry, isRelevant: true, isUserUpdated: false } };
    });
  };

  const clearTranslation = (entityKey) => {
    setTranslations((prev) => {
      const next = { ...prev };
      appliedLanguages.forEach((language) => delete next[`${entityKey}:${language}`]);
      return next;
    });
  };

  const startTranslationRequest = () => {
    translationRequestCountRef.current += 1;
    setIsTranslating(true);
  };

  const finishTranslationRequest = () => {
    translationRequestCountRef.current = Math.max(0, translationRequestCountRef.current - 1);
    setIsTranslating(translationRequestCountRef.current > 0);
  };

  const resetTranslations = (items) => {
    if (!appliedLanguages.length) return;
    const pendingKeysByLanguage = Object.fromEntries(
      appliedLanguages.map((language) => [language, getTranslationKeysFromItems(items, [language])])
    );
    const pendingKeys = Object.values(pendingKeysByLanguage).flat();
    addPendingTranslationKeys(setPendingManualTranslationKeys, pendingKeys);
    startTranslationRequest();
    Promise.allSettled(
      appliedLanguages.map((language) =>
        translateBulk({ fromLang: sourceLanguage, isRichText: true, items, toLang: language })
          .then((translatedItems) => {
            if (isMountedRef.current) {
              setTranslations((prev) => applyTranslatedItems(translatedItems, prev, language));
            } else {
              // Modal closed mid-request: keep the triggered translation by writing it straight to
              // the module-scoped draft, which survives unmount and is restored on reopen.
              const draft = getTranslationDraft(survey.id) || {};
              setTranslationDraft(
                survey.id,
                applyTranslatedItems(translatedItems, draft, language)
              );
            }
            return { language, translatedItems };
          })
          .finally(() => {
            if (isMountedRef.current) {
              removePendingTranslationKeys(
                setPendingManualTranslationKeys,
                pendingKeysByLanguage[language]
              );
            }
          })
      )
    )
      .then((settled) => {
        const hadRealError = settled.some(
          (outcome) =>
            outcome.status === 'rejected' &&
            outcome.reason?.name !== 'AbortError' &&
            outcome.reason?.name !== 'CanceledError'
        );
        if (isMountedRef.current && hadRealError) {
          toast.error(getTranslationModalMessage('fetchError', sourceLanguage));
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          finishTranslationRequest();
        }
      });
  };

  // Concepts translate on demand, so reset removes their local translations.
  const resetConcepts = () => {
    setTranslations((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([key]) => !key.startsWith('concept-')))
    );
  };

  const resetQuestion = (questionId) => {
    const question = syncedQuestions.find(({ id }) => id === questionId);
    if (!question) return;
    const entry = {};
    const questionSource = question.richText || question.text;
    if (questionSource) entry.text = questionSource;
    if (isMaxDiffQuestion(question)) {
      const properties = {};
      if (question.properties?.bestLabel) properties.bestLabel = question.properties.bestLabel;
      if (question.properties?.worstLabel) properties.worstLabel = question.properties.worstLabel;
      if (Object.keys(properties).length) entry.properties = properties;
    }
    const optionItems = {};
    (question.answers || []).forEach((answer) => {
      const optionSource = answer.richText || answer.text;
      if (optionSource) optionItems[answer.id] = optionSource;
    });
    if (Object.keys(optionItems).length) entry.options = optionItems;
    const subQuestionItems = {};
    if (QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question.type)) {
      (question.subQuestions || []).forEach((subQuestion) => {
        const subQuestionSource = subQuestion.richText || subQuestion.text;
        if (subQuestionSource) subQuestionItems[subQuestion.id] = subQuestionSource;
      });
    }
    if (Object.keys(subQuestionItems).length) entry.subQuestions = subQuestionItems;
    if (Object.keys(entry).length) resetTranslations({ questions: { [questionId]: entry } });
  };

  // Apply only translations here; language selection was already committed through screener sync.
  // Clear the draft only after the parent confirms persistence.
  const handleApply = async () => {
    if (isApplying || isTranslationLoading || languageSyncStatus !== 'idle') return;

    setIsBaseMenuOpen(false);
    setIsLanguageMenuOpen(false);
    setIsApplying(true);
    let didPersist = false;
    try {
      didPersist =
        (await onSubmit({
          ...buildSurveyUpdates(translations, {
            appliedLanguages,
            concepts,
            questions: syncedQuestions,
          }),
        })) === true;
    } catch (error) {
      console.error('Error applying translation changes', error);
    }

    if (didPersist) {
      clearTranslationDraft(survey.id);
      return;
    }
    if (isMountedRef.current) setIsApplying(false);
  };

  // Concepts translate on demand and are committed only on Apply.
  const translateConcept = (conceptId, language) => {
    const entityKey = `concept-${conceptId}`;
    const inFlightKey = `${entityKey}:${language}`;
    if (conceptTranslationInFlightRef.current.has(inFlightKey)) return;
    const conceptName = concepts.find((concept) => concept.id === conceptId)?.conceptName?.trim();
    if (!conceptName) return;
    conceptTranslationInFlightRef.current.add(inFlightKey);
    addPendingTranslationKeys(setPendingManualTranslationKeys, [inFlightKey]);
    startTranslationRequest();
    translateBulk({
      fromLang: sourceLanguage,
      isRichText: true,
      items: { concepts: { [conceptId]: conceptName } },
      toLang: language,
    })
      .then((translatedItems) => {
        // Merge onto the latest buffer so concurrent concept translations do not clobber each other.
        if (isMountedRef.current) {
          setTranslations((prev) => applyTranslatedItems(translatedItems, prev, language));
        } else {
          // Keep the user-triggered result in the module-scoped draft after close.
          const draft = getTranslationDraft(survey.id) || {};
          setTranslationDraft(survey.id, applyTranslatedItems(translatedItems, draft, language));
        }
      })
      .catch((error) => {
        if (
          isMountedRef.current &&
          error?.name !== 'AbortError' &&
          error?.name !== 'CanceledError'
        ) {
          toast.error(getTranslationModalMessage('fetchError', sourceLanguage));
        }
      })
      .finally(() => {
        conceptTranslationInFlightRef.current.delete(inFlightKey);
        if (isMountedRef.current) {
          removePendingTranslationKeys(setPendingManualTranslationKeys, [inFlightKey]);
          finishTranslationRequest();
        }
      });
  };

  // Required auto-translated strings before Apply is allowed; concepts are opt-in.
  const requiredEntityKeys = useMemo(() => {
    const keys = [];
    activeQuestions.forEach((question) => {
      // The language screener is curated, so it never gates Apply Changes.
      if (isLanguageScreenerQuestion(question)) return;
      if (question.richText || question.text) keys.push(`question-${question.id}`);
      if (isMaxDiffQuestion(question)) {
        if (question.properties?.bestLabel) keys.push(`maxdiff-best-label-${question.id}`);
        if (question.properties?.worstLabel) keys.push(`maxdiff-worst-label-${question.id}`);
      }
      (question.answers || []).forEach((answer) => {
        if (answer._delete) return;
        if (answer.richText || answer.text) keys.push(`option-${question.id}-${answer.id}`);
      });
      // Only grid questions expose subQuestions as translatable row labels.
      if (QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question.type)) {
        (question.subQuestions || []).forEach((subQuestion) => {
          if (subQuestion._delete) return;
          if (subQuestion.richText || subQuestion.text) {
            keys.push(`subquestion-${question.id}-${subQuestion.id}`);
          }
        });
      }
    });
    return keys;
  }, [activeQuestions]);

  // No target languages means there is no translation work blocking Apply.
  const hasAllTranslations = useMemo(
    () =>
      !appliedLanguages.length ||
      requiredEntityKeys.every((entityKey) =>
        appliedLanguages.every((language) => {
          const entry = translations[`${entityKey}:${language}`];
          return hasTranslationText(entry?.richText) && entry?.isRelevant !== false;
        })
      ),
    [appliedLanguages, requiredEntityKeys, translations]
  );

  const actionButtons = [
    <Button
      isDisabled={isApplying}
      key="modal-cancel-button"
      onClick={onClose}
      text="Cancel"
      variant="secondary"
    />,
    <Button
      data-testid="modal-translation-apply"
      isDisabled={
        !hasAllTranslations || isApplying || isTranslationLoading || languageSyncStatus !== 'idle'
      }
      isLoading={languageSyncStatus === 'pending' || isApplying}
      key="modal-confirm-button"
      onClick={handleApply}
      text="Apply Changes"
    />,
  ];

  return (
    <Modal
      buttons={actionButtons}
      id="modal-translation"
      isActive
      onClose={onClose}
      size={appliedLanguages.length > 1 ? 'x-large' : 'large'}
      title="Translation"
    >
      <div
        className={styles['modal-translation']}
        ref={containerRef}
        style={{ '--modal-translation-target-count': Math.max(appliedLanguages.length, 1) }}
      >
        <div className={styles['modal-translation-language']}>
          <div className={styles['modal-translation-language-actions']}>
            <Button
              buttonRef={baseLanguageTarget}
              className={styles['modal-translation-language-button']}
              isDisabled={languageSyncStatus === 'pending' || isApplying}
              onClick={openBaseLanguageMenu}
              text={`Language: ${baseLanguageLabel}`}
              variant="secondary"
            />
            <Button
              buttonRef={languageMenuTarget}
              className={styles['modal-translation-language-button']}
              isDisabled={languageSyncStatus === 'pending' || isApplying}
              onClick={openLanguageMenu}
              text="Translate"
              variant="secondary"
            />
          </div>
          {appliedLanguages.length > 0 && (
            <>
              <div className={styles['modal-translation-language-columns']}>
                {appliedLanguages.map((language) => (
                  <Select
                    className={styles['modal-translation-language-select']}
                    isDisabled={languageSyncStatus === 'pending' || isApplying}
                    key={language}
                    onChange={(option) => changeAppliedLanguage(language, option)}
                    options={languageOptionsFor(language)}
                    value={getLanguageOption(language)}
                  />
                ))}
              </div>
              <span aria-hidden className={styles['modal-translation-language-columns-spacer']} />
            </>
          )}
          <OverlayPopover
            className={styles['modal-translation-language-menu']}
            header="Available Languages"
            isActive={isBaseMenuOpen}
            onHide={() => setIsBaseMenuOpen(false)}
            placement="bottom-start"
            target={baseLanguageTarget.current}
          >
            <div className={styles['modal-translation-language-menu-list']}>
              {baseLanguageOptions.map(({ label, value }) => (
                <Radio
                  isChecked={(pendingBaseLanguage || '') === value}
                  key={value || 'default'}
                  label={label}
                  name="base-language"
                  onChange={() => setPendingBaseLanguage(value)}
                  value={value}
                />
              ))}
            </div>
            <Button
              className={styles['modal-translation-language-menu-apply']}
              onClick={applyBaseLanguage}
              text="Apply"
              variant="secondary"
            />
          </OverlayPopover>
          <OverlayPopover
            className={styles['modal-translation-language-menu']}
            header="Available Languages"
            isActive={isLanguageMenuOpen}
            onHide={() => setIsLanguageMenuOpen(false)}
            placement="bottom-start"
            target={languageMenuTarget.current}
          >
            <div className={styles['modal-translation-language-menu-checkboxes']}>
              {languageOptions.map(({ label, value }) => (
                <Checkbox
                  isChecked={pendingLanguages.includes(value)}
                  isDisabled={
                    value === sourceLanguage ||
                    (pendingLanguages.length >= MAX_LANGUAGES && !pendingLanguages.includes(value))
                  }
                  key={value}
                  label={label}
                  onChange={() => togglePendingLanguage(value)}
                  value={value}
                />
              ))}
            </div>
            <Button
              className={styles['modal-translation-language-menu-apply']}
              onClick={applyLanguages}
              text="Apply"
              variant="secondary"
            />
          </OverlayPopover>
        </div>

        <ConceptTranslations
          appliedLanguages={appliedLanguages}
          concepts={concepts}
          getEntry={getEntry}
          getTranslation={getTranslation}
          hasConceptName={hasConceptName}
          isDisabled={isApplying}
          languageLabels={languageLabels}
          onClear={clearTranslation}
          onDismissRelevanceWarning={dismissRelevanceWarning}
          onReset={resetConcepts}
          onTranslate={translateConcept}
          onTranslationChange={handleTranslationChange}
          pendingTranslationKeys={pendingTranslationKeys}
          sourceLanguage={sourceLanguage}
        />

        {activeQuestions.map((question, index) => (
          <QuestionTranslations
            appliedLanguages={appliedLanguages}
            getEntry={getEntry}
            getTranslation={getTranslation}
            hasConceptName={hasConceptName}
            index={index}
            isDisabled={isApplying}
            key={question.id}
            languageLabels={languageLabels}
            onClear={clearTranslation}
            onDismissRelevanceWarning={dismissRelevanceWarning}
            onPrioritizeQuestion={prioritizeQuestion}
            onReset={resetQuestion}
            onTranslationChange={handleTranslationChange}
            pendingTranslationKeys={pendingTranslationKeys}
            question={question}
            registerQuestion={registerQuestion}
            sourceLanguage={sourceLanguage}
          />
        ))}
      </div>
    </Modal>
  );
};

ModalTranslation.propTypes = {
  onClose: PropTypes.func.isRequired,
  onLanguageInfoChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  survey: PropTypes.object.isRequired,
};

export default ModalTranslation;
